import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate } from "@/lib/format";
import { API } from "@/lib/api";

const TYPE_LABEL = { preventif: "Pemeliharaan Preventif", perbaikan: "Perbaikan (Breakdown)" };

let _logoPromise;
function loadLogoDataUrl() {
  if (!_logoPromise) {
    _logoPromise = fetch("/logo.png")
      .then((r) => r.blob())
      .then((b) => new Promise((res) => {
        const fr = new FileReader();
        fr.onloadend = () => res(fr.result);
        fr.onerror = () => res(null);
        fr.readAsDataURL(b);
      }))
      .catch(() => null);
  }
  return _logoPromise;
}

// Berita Acara Pemeliharaan (BAP) for a single service record.
const fmtDateTime = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export async function generateServicePDF(sv, { isAdmin }) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  // Watermark logo (drawn first so content sits on top)
  const logo = await loadLogoDataUrl();
  if (logo) {
    try {
      const iw = 120, ih = 120;
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.06 }));
      doc.addImage(logo, "PNG", (w - iw) / 2, (h - ih) / 2, iw, ih);
      doc.restoreGraphicsState();
    } catch (e) {}
  }

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("BERITA ACARA PEMELIHARAAN (BAP)", 14, 13);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text("SATRIA ENGINEERING - Sistem Informasi Pemeliharaan Mesin", 14, 21);

  doc.setTextColor(0, 0, 0);
  let y = 40;
  const rows = [
    ["Mesin", sv.machine_name || "-"],
    ["Tanggal", formatDate(sv.date)],
    ["Jenis", TYPE_LABEL[sv.service_type] || sv.service_type],
    ["Teknisi", sv.technician_name || "-"],
    ["Operator", sv.operator_name || "-"],
    ["Deskripsi Masalah", sv.problem || "-"],
    ["Tindakan", sv.action || "-"],
    ["Status", sv.status === "selesai" ? "Selesai" : "Dalam Proses"],
    ["Mulai Perbaikan", fmtDateTime(sv.repair_start)],
    ["Selesai Perbaikan", fmtDateTime(sv.repair_end)],
    ["Waktu Pengerjaan", `${sv.repair_duration_hours || 0} jam`],
    ["Total Downtime", `${sv.downtime_hours || 0} jam`],
  ];

  autoTable(doc, {
    startY: y,
    head: [["Keterangan", "Detail"]],
    body: rows,
    theme: "grid",
    headStyles: { fillColor: [2, 132, 199] },
    columnStyles: { 0: { cellWidth: 55, fontStyle: "bold" } },
  });
  y = doc.lastAutoTable.finalY + 8;

  if ((sv.used_parts || []).length > 0) {
    autoTable(doc, {
      startY: y,
      head: [["Sparepart Digunakan", "Qty"]],
      body: sv.used_parts.map((p) => [p.name, String(p.qty)]),
      theme: "striped",
      headStyles: { fillColor: [71, 85, 105] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Signatures
  y += 4;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text("Operator", 40, y, { align: "center" });
  doc.text("Teknisi", w - 55, y, { align: "center" });
  const sigY = y + 4;
  try {
    if (sv.operator_signature) doc.addImage(sv.operator_signature, "PNG", 15, sigY, 50, 22);
    if (sv.technician_signature) doc.addImage(sv.technician_signature, "PNG", w - 80, sigY, 50, 22);
  } catch (e) {}
  const lineY = sigY + 26;
  doc.setDrawColor(0);
  doc.line(15, lineY, 65, lineY);
  doc.line(w - 80, lineY, w - 30, lineY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.text(sv.operator_name || "( ................. )", 40, lineY + 5, { align: "center" });
  doc.text(sv.technician_name || "( ................. )", w - 55, lineY + 5, { align: "center" });

  // Foto dokumentasi perbaikan
  const photos = sv.photos || [];
  if (photos.length > 0) {
    const pageH = doc.internal.pageSize.getHeight();
    let py = lineY + 16;
    if (py > pageH - 70) { doc.addPage(); py = 20; }
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("Foto Dokumentasi:", 15, py);
    py += 6;
    const imgW = 55, imgH = 55, gap = 8;
    let px = 15;
    for (const ph of photos) {
      const du = await fetchImageDataUrl(ph.file_id);
      if (!du) continue;
      if (px + imgW > w - 15) { px = 15; py += imgH + gap; }
      if (py + imgH > pageH - 15) { doc.addPage(); py = 20; px = 15; }
      try { doc.addImage(du, du.includes("image/png") ? "PNG" : "JPEG", px, py, imgW, imgH); } catch (e) {}
      px += imgW + gap;
    }
  }

  doc.save(`BAP-${(sv.machine_name || "servis").replace(/\s+/g, "-")}-${formatDate(sv.date)}.pdf`);
}

// Berita Acara Checksheet Pemeliharaan for a schedule.
export async function generateChecksheetPDF(sch) {
  const cs = sch.checksheet || {};
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  const logo = await loadLogoDataUrl();
  if (logo) {
    try {
      const iw = 120, ih = 120;
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.06 }));
      doc.addImage(logo, "PNG", (w - iw) / 2, (h - ih) / 2, iw, ih);
      doc.restoreGraphicsState();
    } catch (e) {}
  }

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.text("BERITA ACARA CHECKSHEET PEMELIHARAAN", 14, 12);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text("SATRIA ENGINEERING - Sistem Informasi Pemeliharaan Mesin", 14, 20);

  doc.setTextColor(0, 0, 0);
  const info = [
    ["Mesin", sch.machine_name || "-"],
    ["Jenis Pemeliharaan", sch.maintenance_type || "-"],
    ["Jatuh Tempo", formatDate(sch.due_date)],
    ["Teknisi", cs.technician_name || sch.technician_name || "-"],
    ["Operator", cs.operator_name || "-"],
  ];
  autoTable(doc, {
    startY: 34, body: info, theme: "plain",
    columnStyles: { 0: { cellWidth: 50, fontStyle: "bold" } }, styles: { fontSize: 9 },
  });
  let y = doc.lastAutoTable.finalY + 4;

  const RES = { ok: "OK", not_ok: "Tidak OK" };
  const items = cs.items || [];
  let _n = 0;
  autoTable(doc, {
    startY: y,
    head: [["No", "Item Pemeriksaan", "Nilai", "Satuan", "Standar", "Hasil", "Catatan"]],
    body: items.map((it) => [
      it.is_sub ? "" : String(++_n),
      it.is_sub ? `      ${it.item || "-"}` : (it.item || "-"),
      it.value || "-", it.unit || "-",
      (it.std_min || it.std_max) ? `${it.std_min || ""} - ${it.std_max || ""}` : "-",
      RES[it.result] || "-", it.note || "-",
    ]),
    theme: "grid",
    headStyles: { fillColor: [2, 132, 199] },
    styles: { fontSize: 8, cellPadding: 1.5 },
    columnStyles: { 0: { cellWidth: 10 }, 5: { cellWidth: 18 } },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 5) {
        const raw = items[d.row.index]?.result;
        if (raw === "ok") d.cell.styles.textColor = [5, 150, 105];
        if (raw === "not_ok") d.cell.styles.textColor = [225, 29, 72];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  const okCount = items.filter((i) => i.result === "ok").length;
  const notOkCount = items.filter((i) => i.result === "not_ok").length;
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.text(`Ringkasan: ${okCount} OK, ${notOkCount} Tidak OK, dari ${items.length} item.`, 14, y);
  y += 8;

  if (cs.note) {
    doc.setFont(undefined, "bold");
    doc.text("Catatan:", 14, y);
    y += 5;
    doc.setFont(undefined, "normal");
    const noteLines = doc.splitTextToSize(cs.note, w - 28);
    doc.text(noteLines, 14, y);
    y += noteLines.length * 5 + 4;
  } else {
    y += 2;
  }

  if (y > h - 60) { doc.addPage(); y = 20; }
  doc.setFont(undefined, "bold");
  doc.setFontSize(10);
  doc.text("Operator", 40, y, { align: "center" });
  doc.text("Teknisi", w - 55, y, { align: "center" });
  const sigY = y + 4;
  try {
    if (cs.operator_signature) doc.addImage(cs.operator_signature, "PNG", 15, sigY, 50, 22);
    if (cs.technician_signature) doc.addImage(cs.technician_signature, "PNG", w - 80, sigY, 50, 22);
  } catch (e) {}
  const lineY = sigY + 26;
  doc.setDrawColor(0);
  doc.line(15, lineY, 65, lineY);
  doc.line(w - 80, lineY, w - 30, lineY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.text(cs.operator_name || "( ................. )", 40, lineY + 5, { align: "center" });
  doc.text(cs.technician_name || sch.technician_name || "( ................. )", w - 55, lineY + 5, { align: "center" });

  doc.save(`BAP-Checksheet-${(sch.machine_name || "mesin").replace(/\s+/g, "-")}-${formatDate(sch.due_date)}.pdf`);
}

// Rekap ringkasan seluruh checksheet terisi dalam satu dokumen.
export async function generateRecapPDF(schedules) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.text("REKAP RIWAYAT PREVENTIF", 14, 12);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text("SATRIA ENGINEERING - Sistem Informasi Pemeliharaan Mesin", 14, 20);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(`Total checksheet terisi: ${schedules.length}`, 14, 36);

  const RES = { ok: "OK", not_ok: "Tidak OK" };
  autoTable(doc, {
    startY: 40,
    head: [["No", "Mesin", "Jenis", "Jatuh Tempo", "Disimpan", "OK", "Tidak OK", "Operator", "Teknisi", "Catatan"]],
    body: schedules.map((s, i) => {
      const cs = s.checksheet || {};
      const items = cs.items || [];
      const ok = items.filter((x) => x.result === "ok").length;
      const notOk = items.filter((x) => x.result === "not_ok").length;
      return [
        String(i + 1), s.machine_name || "-", s.maintenance_type || "-",
        formatDate(s.due_date), cs.updated_at ? formatDate(cs.updated_at) : "-",
        String(ok), String(notOk), cs.operator_name || "-",
        cs.technician_name || s.technician_name || "-", cs.note || "-",
      ];
    }),
    theme: "grid",
    headStyles: { fillColor: [2, 132, 199] },
    styles: { fontSize: 8, cellPadding: 1.5, valign: "top" },
    columnStyles: { 0: { cellWidth: 8 }, 5: { cellWidth: 10 }, 6: { cellWidth: 14 }, 9: { cellWidth: 34 } },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 6 && d.cell.raw !== "0") d.cell.styles.textColor = [225, 29, 72];
      if (d.section === "body" && d.column.index === 5 && d.cell.raw !== "0") d.cell.styles.textColor = [5, 150, 105];
    },
  });

  doc.save(`Rekap-Riwayat-Preventif-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateReportPDF(services, { isAdmin }, prevHistory = []) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, 24, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.text("LAPORAN RIWAYAT SERVIS", 14, 12);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text(`Dicetak: ${formatDate(new Date().toISOString())}`, 14, 19);
  doc.setTextColor(0, 0, 0);

  const head = ["Tanggal", "Mesin", "Jenis", "Teknisi", "Status", "Waktu (jam)"];
  const body = services.map((s) => {
    const r = [
      formatDate(s.date), s.machine_name, TYPE_LABEL[s.service_type] || s.service_type,
      s.technician_name || "-", s.status === "selesai" ? "Selesai" : "Dalam Proses",
      String(s.downtime_hours || 0),
    ];
    return r;
  });

  autoTable(doc, {
    startY: 30, head: [head], body, theme: "striped",
    headStyles: { fillColor: [2, 132, 199] }, styles: { fontSize: 8 },
  });

  {
    const total = services.reduce((a, s) => a + (s.downtime_hours || 0), 0);
    doc.setFont(undefined, "bold");
    doc.text(`Total Downtime: ${total} jam`, 14, doc.lastAutoTable.finalY + 10);
  }

  if (prevHistory && prevHistory.length > 0) {
    const startY = doc.lastAutoTable.finalY + (isAdmin ? 16 : 10);
    doc.setFont(undefined, "bold");
    doc.setFontSize(12);
    doc.text("RIWAYAT PREVENTIF (CHECKSHEET)", 14, startY);
    autoTable(doc, {
      startY: startY + 4,
      head: [["Mesin", "Jenis", "Jatuh Tempo", "OK", "Tidak OK", "Operator", "Teknisi"]],
      body: prevHistory.map((p) => [
        p.machine_name || "-", p.maintenance_type || "-", formatDate(p.due_date),
        String(p.ok || 0), String(p.not_ok || 0), p.operator_name || "-",
        p.technician_name || "-",
      ]),
      theme: "striped",
      headStyles: { fillColor: [2, 132, 199] },
      styles: { fontSize: 8 },
      didParseCell: (d) => {
        if (d.section === "body" && d.column.index === 4 && d.cell.raw !== "0") d.cell.styles.textColor = [225, 29, 72];
        if (d.section === "body" && d.column.index === 3 && d.cell.raw !== "0") d.cell.styles.textColor = [5, 150, 105];
      },
    });
  }

  doc.save(`Laporan-Servis-${Date.now()}.pdf`);
}

async function fetchImageDataUrl(fileId) {
  try {
    const res = await fetch(`${API}/files/${fileId}/download`, { credentials: "include" });
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

// Purchase Request (Order Sparepart) for the purchasing team.
export async function generateOrderPDF(order) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  const logo = await loadLogoDataUrl();
  if (logo) {
    try {
      const iw = 120, ih = 120;
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.06 }));
      doc.addImage(logo, "PNG", (w - iw) / 2, (h - ih) / 2, iw, ih);
      doc.restoreGraphicsState();
    } catch (e) {}
  }

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("PERMINTAAN PEMBELIAN SPAREPART", 14, 13);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text("SATRIA ENGINEERING - Purchase Request (untuk Purchasing)", 14, 21);

  doc.setTextColor(0, 0, 0);
  const info = [
    ["No. Order", order.order_no || "-"],
    ["Tanggal", formatDate(order.date)],
    ["Pemohon", order.requester || "-"],
    ["Supplier", order.supplier || "-"],
    ["Dibutuhkan", order.needed_by ? formatDate(order.needed_by) : "-"],
    ["Prioritas", order.priority === "mendesak" ? "MENDESAK" : "Normal"],
    ["Status", order.status || "baru"],
  ];
  autoTable(doc, {
    startY: 40,
    head: [["Keterangan", "Detail"]],
    body: info,
    theme: "grid",
    headStyles: { fillColor: [2, 132, 199] },
    styles: { fontSize: 9 },
    columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" } },
  });

  const items = order.items || [];
  const itemImgs = await Promise.all(
    items.map((it) => (it.image?.file_id ? fetchImageDataUrl(it.image.file_id) : Promise.resolve(null)))
  );
  const imgCol = 2;
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 6,
    head: [["No", "Nama Sparepart", "Gambar", "Kode", "Qty", "Satuan", "Keterangan"]],
    body: items.map((it, i) => [
      String(i + 1), it.name || "-", "", it.code || "-", String(it.qty || 0), it.unit || "-", it.note || "-",
    ]),
    theme: "striped",
    headStyles: { fillColor: [2, 132, 199] },
    styles: { fontSize: 9, valign: "middle" },
    columnStyles: { 0: { cellWidth: 10 }, 2: { cellWidth: 26 }, 4: { cellWidth: 14 }, 5: { cellWidth: 18 } },
    didParseCell: (data) => {
      if (data.section === "body" && itemImgs[data.row.index]) data.cell.styles.minCellHeight = 26;
    },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === imgCol) {
        const du = itemImgs[data.row.index];
        if (du) {
          const pad = 1.5;
          const size = Math.min(data.cell.width, data.cell.height) - pad * 2;
          const x = data.cell.x + (data.cell.width - size) / 2;
          const y = data.cell.y + (data.cell.height - size) / 2;
          try { doc.addImage(du, du.includes("image/png") ? "PNG" : "JPEG", x, y, size, size); } catch (e) {}
        }
      }
    },
  });

  let y = doc.lastAutoTable.finalY + 8;
  if (order.notes) {
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("Catatan:", 14, y);
    doc.setFont(undefined, "normal");
    const lines = doc.splitTextToSize(order.notes, w - 28);
    doc.text(lines, 14, y + 5);
    y += 5 + lines.length * 5 + 4;
  }

  const images = order.images || [];
  if (images.length > 0) {
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("Foto Sparepart:", 14, y);
    y += 6;
    const imgW = 55, imgH = 55, gap = 8;
    let x = 14;
    for (const im of images) {
      const dataUrl = await fetchImageDataUrl(im.file_id);
      if (!dataUrl) continue;
      if (y + imgH > h - 14) { doc.addPage(); y = 20; x = 14; }
      if (x + imgW > w - 14) { x = 14; y += imgH + gap; if (y + imgH > h - 14) { doc.addPage(); y = 20; } }
      try {
        const fmt = dataUrl.includes("image/png") ? "PNG" : "JPEG";
        doc.addImage(dataUrl, fmt, x, y, imgW, imgH);
      } catch (e) {}
      x += imgW + gap;
    }
  }

  doc.save(`Order-${order.order_no || Date.now()}.pdf`);
}
